import { useState } from 'react';
import Modal from 'components/shared/modal';
import { motion, useMotionValue } from 'framer-motion';
import { useFormik } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import { appAxios } from 'api/axios';
import { getTokenDetails } from 'functions/userSession';
import { decodeToken } from 'react-jwt';
import { TokenType } from 'types/jwt';
import { Loader, X } from 'lucide-react';
import { formatToNaira } from 'functions/currencyConveter';
import { sendCatchFeedback, sendFeedback } from 'functions/feedback';
import CircularProgress from 'components/shared/animations/check';
import Button from 'components/shared/button';
import { invalidateQueries } from 'functions/queryFunc';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  id?: string;
};

const validationSchema = yup.object({
  amount: yup.number().required('Amount is required'),
});

function WithdrawSavingsPopUs({ isOpen, onClose, id }: Props) {
  // const [prompIsOpen, setPrompIsOpen] = useState(true);
  const token = getTokenDetails();
  const decodedToken = decodeToken<TokenType>(token!);
  const customerId = decodedToken?.customerId;
  const [loading, setLoading] = useState(false);
  const [isModalSuccessOpen, setIsModalSuccessOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      customerId: customerId,
      amount: '',
      savingsGoalId: id,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const { customerId, amount, savingsGoalId } = values;
        const { data } = await appAxios.post('/with-draw-savings', {
          customerId,
          amount,
          savingsGoalId,
        });
        if (data.statusCode === 200) {
          sendFeedback(data?.message, 'success');
          setIsModalSuccessOpen(true);
          invalidateQueries([
            'totalSavingsBalance',
            'getCustomerSavings',
            'savingsHistory',
          ]);
        } else sendCatchFeedback(data);
      } catch (error) {
        console.error('API error:', error);
        sendCatchFeedback(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleProceed = () => {
    setIsModalSuccessOpen(false);
    onClose();
  };
  const progress = useMotionValue(90);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Withdraw Savings"
        subtitle="Please enter your details to withdraw your savings"
        width="400px"
      >
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <InputField
            name="amount"
            label="Amount to withdraw"
            type="number"
            placeholder="Enter amount to withdraw"
            value={formik.values.amount}
            // onChange={formik.handleChange}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              // Allow only numbers and decimals
              if (/^\d*\.?\d*$/.test(value)) {
                formik.setFieldValue('amount', value);
              }
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.amount ? formik.errors.amount : ''}
            maxLength={19}
          />
          {/* <InputField
          name="passcode"
          label="Passcode"
          placeholder="Enter your 4 digits transaction pin"
          type="password"
          value={formik.values.passcode}
          // onChange={formik.handleChange}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            if (/^\d*$/.test(value) && value.length <= 4) {
              formik.setFieldValue('passcode', value);
            }
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.passcode ? formik.errors.passcode : ''}
        /> */}
          <div className="flex gap-x-4 mt-4 mb-6">
            <button
              type="button"
              className="flex-1 border border-[#EFA005] outline-none rounded-lg text-[#EFA005] p-1"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 border border-[#EFA005] outline-none rounded-lg bg-[#EFA005] text-white p-1"
              disabled={loading}
            >
              {loading ? (
                <Loader className="mx-auto animate-spin" size={16} />
              ) : (
                'Withdraw'
              )}
            </button>
          </div>
        </form>
      </Modal>
      {isModalSuccessOpen && (
        <article className="inset-0 flex z-[9999] justify-center items-center fixed bg-black/30">
          <div className="w-[90vw] bg-white rounded-md max-w-md p-6 ">
            <button
              type="button"
              className="absolute top-3 right-3 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex justify-center items-center"
              onClick={handleProceed}
            >
              <X />
            </button>
            <div className="p-4 text-center">
              <div className="flex items-center justify-center my-4">
                <motion.div
                  initial={{ x: 0 }}
                  animate={{ x: 100 }}
                  style={{ x: progress }}
                  transition={{ duration: 1 }}
                />
                <CircularProgress progress={progress} />
              </div>
              <h3 className="mb-5 text-lg font-bold ">
                Withdrawal Successful!
              </h3>
              <div className="mb-4">
                <p>
                  You have successfully withdraw the sum of{' '}
                  <span className="font-semibold">
                    {formatToNaira(Number(formik.values.amount))}
                  </span>
                  . Thank you!
                </p>
              </div>
              <div className="flex justify-center gap-4">
                <Button
                  type="button"
                  onClick={handleProceed}
                  className="mt-6 w-full max-w-sm"
                >
                  Proceed
                </Button>
              </div>
            </div>
          </div>
        </article>
      )}
    </>
  );
}

export default WithdrawSavingsPopUs;
